'use client';

import React, { useEffect, useRef } from 'react';

interface Step {
  icon: React.ReactNode;
  number: string;
  title: string;
  description: string;
}

const steps: Step[] = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    number: '01',
    title: 'Discovery & Strategy',
    description: 'We scope your app or website requirements, define your target audience, and map out a paid ads strategy across Meta, TikTok, and Google — all within 48 hours.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    number: '02',
    title: 'Design & Build',
    description: 'We design and develop your mobile app or website — pixel-perfect UI, fast-loading, mobile-first, and built for conversion from the ground up.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 2L11 13" />
        <path d="M22 2L15 22 11 13 2 9l20-7z" />
      </svg>
    ),
    number: '03',
    title: 'Launch Ads Campaigns',
    description: 'Meta, TikTok, and Google Ads campaigns go live simultaneously — driving paid traffic to your app or website from day one with creative that converts.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
    number: '04',
    title: 'Optimise & Scale',
    description: 'We analyse ROAS, conversion rates, and user retention weekly — cutting waste, scaling winning creatives, and growing your revenue month over month.',
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, transparent 0%, rgba(245,166,35,0.03) 50%, transparent 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-accent/20 bg-accent/5 mb-6">
            <span className="text-xs font-700 text-accent tracking-widest uppercase">How It Works</span>
          </div>
          <h2
            className="font-display font-800 text-foreground tracking-tight mb-4"
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.15,
            }}
          >
            From Idea to{' '}
            <span className="text-gradient-amber">Revenue</span>
          </h2>
          <p className="text-muted font-400 leading-relaxed">
            A focused four-step process that gets your app or website live, visible, and earning — fast.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`relative glass-card glass-card-hover rounded-3xl p-7 reveal reveal-delay-${i + 1}`}
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 -right-2.5 w-5 h-px bg-accent/20 z-10" />
              )}
              <div className="flex items-start justify-between mb-5">
                <div className="w-11 h-11 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                  {step.icon}
                </div>
                <span
                  className="text-4xl font-800 font-display text-foreground/5 leading-none"
                  style={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800 }}
                >
                  {step.number}
                </span>
              </div>
              <h3 className="text-base font-700 text-foreground mb-2">{step.title}</h3>
              <p className="text-sm text-muted leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
